/**
 * useLastPlayedChronicle — finds the most recently saved (still in-progress)
 * chronicle save and returns enough info to render a "Continue where you left
 * off" banner. Works by scanning every chronicle save key in localStorage
 * and picking the one with the most recent `savedAt` timestamp.
 */
import { useMemo } from "react";
import { chronicles } from "@/data/chronicles";
import { CHRONICLE_SAVE_KEYS } from "@/data/chronicles/save-keys";

/** Mapping of chronicle id → engine save key in localStorage. Derived
 *  from the canonical `CHRONICLE_SAVE_KEYS` map in
 *  `src/data/chronicles/save-keys.ts` so it can never drift out of
 *  sync as new chronicles ship (a class of bug we hit twice: the
 *  Continue banner on the home page silently went missing for nine
 *  chronicles — the-anarchy, wars-of-the-roses, english-civil-wars,
 *  irish-independence, christianization-scandinavia, livonian-crusade,
 *  settlement-of-iceland, kalmar-union, great-northern-war — because
 *  this list was hard-coded and stopped being updated). Exported so
 *  other consumers (useChronicleProgress, RegionMarker) can iterate
 *  the same source of truth. The save-key naming is historical
 *  (engine-prefixed, not chronicle-id-prefixed) so a prefix lookup
 *  like `chronicles_europe_save_${chronicleId}` won't work — it must
 *  go through this map. */
export const SAVE_KEYS: Array<{ chronicleId: string; key: string }> = Object.entries(
  CHRONICLE_SAVE_KEYS,
).map(([chronicleId, key]) => ({ chronicleId, key }));

export interface LastPlayed {
  chronicleId: string;
  chronicleTitle: string;
  characterName: string;
  year: number;
  season: string;
  savedAt: string;
  decisionsMade: number;
  isComplete: boolean;
}

export function useLastPlayedChronicle(): LastPlayed | null {
  return useMemo(() => {
    let best: LastPlayed | null = null;
    let bestTime = 0;

    for (const { chronicleId, key } of SAVE_KEYS) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw);
        const t = data.savedAt ? Date.parse(data.savedAt) : 0;
        if (!t || t <= bestTime) continue;
        // Skip zero-decision saves. The Play page autosaves the
        // initial GameState the moment the player loads a chronicle,
        // before any choices are made. After a "Reset all progress",
        // a player who simply opens a chronicle to verify the reset
        // would otherwise see Continue resurface — the save exists
        // but the player has done nothing yet. Only treat a save as
        // genuinely in-progress when at least one choice has been
        // committed to the choiceHistory.
        const decisions = Array.isArray(data.choiceHistory)
          ? data.choiceHistory.length
          : 0;
        if (decisions === 0 && data.isComplete !== true) continue;
        const chronicle = chronicles.find((c) => c.id === chronicleId);
        if (!chronicle) continue;
        bestTime = t;
        best = {
          chronicleId,
          chronicleTitle: chronicle.title,
          characterName: data.characterName ?? "",
          year: data.time?.year ?? chronicle.dateRange[0],
          season: data.time?.season ?? "",
          savedAt: data.savedAt,
          decisionsMade: Array.isArray(data.choiceHistory) ? data.choiceHistory.length : 0,
          // Completed runs are surfaced too so the home screen can show a
          // non-clickable "Last played" badge instead of a "Continue"
          // shortcut — preventing endgame-only replay shortcuts.
          isComplete: !!data.isComplete,
        };
      } catch {
        // skip malformed save
      }
    }

    return best;
  }, []);
}
